import { createContext, useContext, useState, useCallback, useMemo } from "react";
import { WA_NUMBER } from "../data/menuData";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // {id, name, price, qty}
  const [open, setOpen] = useState(false);

  const addItem = useCallback((item, delta = 1) => {
    const id = item.name;
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx >= 0) {
        const next = [...prev];
        const nq = Math.max(0, next[idx].qty + delta);
        if (nq === 0) return next.filter((_, i) => i !== idx);
        next[idx] = { ...next[idx], qty: nq };
        return next;
      }
      if (delta <= 0) return prev;
      return [
        ...prev,
        { id, name: item.name, price: item.price ?? null, qty: delta },
      ];
    });
  }, []);

  const setQty = useCallback((id, qty) => {
    setItems((prev) => {
      if (qty <= 0) return prev.filter((p) => p.id !== id);
      return prev.map((p) => (p.id === id ? { ...p, qty } : p));
    });
  }, []);

  const removeItem = useCallback(
    (id) => setItems((prev) => prev.filter((p) => p.id !== id)),
    []
  );

  const clear = useCallback(() => setItems([]), []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const total = items.reduce(
      (s, i) => s + (i.price != null ? i.price * i.qty : 0),
      0
    );
    return { count, total };
  }, [items]);

  const qtyOf = useCallback(
    (name) => items.find((i) => i.id === name)?.qty || 0,
    [items]
  );

  const waLink = useMemo(() => {
    if (items.length === 0) return null;
    const lines = items.map(
      (i) =>
        `• ${i.name} × ${i.qty}${i.price != null ? ` — ₹${i.price * i.qty}` : ""}`
    );
    const totalLine =
      totals.total > 0 ? `\n\nTotal: ₹${totals.total}` : "";
    const msg = `Hi KKFC Bangarpet! I would like to order:\n\n${lines.join("\n")}${totalLine}\n\nName: \nAddress: `;
    return `https://wa.me/${WA_NUMBER}?text=${encodeURIComponent(msg)}`;
  }, [items, totals.total]);

  const value = {
    items,
    open,
    setOpen,
    addItem,
    setQty,
    removeItem,
    clear,
    totals,
    qtyOf,
    waLink,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
